/**
 * Alera Preview Transactions Helper
 *
 * Builds preview-scoped per-class transactions for a single Alera shareholder
 * by fetching the Alera CSV (cached per session) and applying the same
 * class-mapping + strike rules used by `newSharesObject` in
 * `getAleraHoldingsFromFile`. Returns a `{ [shareClass]: Transaction[] }` map
 * suitable for `computeShareholderPreview`'s `txOverrides` parameter.
 *
 * Used by `/admin/profile` shareholder-dashboard preview so admins can preview
 * any Alera shareholder's correctly-calculated P-F (and P-A) potential value
 * without mutating the live `clientData.equity[*].transactions` arrays.
 *
 * Alera intentionally does not use the scoped equity-transactions API — the
 * CSV format relies on `clientData.transactions_columns` and Alera-specific
 * class regexes. This helper mirrors those rules locally; it never reaches
 * the scoped endpoint.
 *
 * Constraints (same shape as preview-transactions.js):
 * - NEVER mutates clientData, userData, sessionStorage, or any global state.
 * - NEVER dispatches events.
 * - Admin-preview-only.
 */

import { cleanCSV } from './string-utils.js';

/**
 * In-memory cache keyed by CSV filename. Scoped to the browser tab's session.
 */
const csvRowCache = new Map();

/**
 * Excel column letter -> zero-based index (A=0, B=1, ...).
 */
const colIdx = (letter) => letter.charCodeAt(0) - 65;

/**
 * Build the column-index map from clientData.transactions_columns. Mirrors the
 * loop at index.js:2458-2461, but returns a plain object instead of writing to
 * globalThis so this helper has no side effects.
 *
 * @param {Object} transactionsColumns - { [letter]: key } map from clientData
 * @returns {Object} { EMAIL: number, CLASS: number, ... } (uppercase keys)
 */
function buildColMap(transactionsColumns) {
	const map = {};
	if (!transactionsColumns) return map;
	for (const [letter, key] of Object.entries(transactionsColumns)) {
		map[key.toUpperCase()] = colIdx(letter);
	}
	return map;
}

/**
 * Classify a CSV row into one of the Alera share-class buckets. Mirrors
 * index.js:2694-2699.
 *
 * @returns {string|null} 'CB' | 'C' | 'C-E' | 'P-A' | 'P-F' | "P-F'" | null
 */
function classifyRow(row, cols) {
	const cls = row[cols.CLASS];
	const vest = row[cols.VESTING];
	const ent = row[cols.ENTITY]?.trim();
	if (!cls) return null;
	if (/Profits Interest\s*\(\s*Class\s+(?:C\s*-?\s*)?B\s*\)/i.test(cls)) return 'CB';
	if (/Profits Interest \(Class P\)/.test(cls)) return 'P-A';
	if (/Profits Interest \(Field\)/.test(cls) && vest !== 'Y') return 'P-F';
	if (/Profits Interest \(Field\)/.test(cls) && vest === 'Y') return "P-F'";
	if (/Common/.test(cls) && !ent) return 'C';
	if (/Common/.test(cls) && ent) return 'C-E';
	return null;
}

/**
 * Build a transaction object matching what `newSharesObject` pushes for the
 * given share class. Mirrors the per-class push blocks at index.js:3339-3452.
 *
 * Strike-bearing classes (P-A, P-F) put PRICE_COL into `strikePrice`.
 * P-F' uses PRICE_COL as `sharePrice` with `strikePrice: '0'` (intentional —
 * vested field interest is fully-participating).
 * CB / C / C-E use PRICE_COL as `sharePrice` with `strikePrice: '0'`.
 */
function buildTransaction(row, shareClass, cols, receiver, receiverName) {
	// Defensive cell accessors: cleanCSV does not pad ragged rows, so any cell
	// beyond a row's length is undefined. Treat missing cells as empty string
	// so transaction shape is preserved (skip happens at caller via classify).
	const dateRaw = row[cols.DATE] ?? '';
	const unitsRaw = row[cols.UNITS] ?? '';
	const priceRaw = row[cols.PRICE] ?? '';
	const date = (Date.parse(dateRaw) / 1000).toString();
	const quantity = (Number(unitsRaw.replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0).toString();
	const price = (Number(priceRaw.replace(/[^.\d]/g, '')) || 0).toString();

	const base = {
		date,
		quantity,
		receiver,
		receiverName,
		sender: '',
		senderName: '',
		shareClass,
	};

	switch (shareClass) {
		case 'CB':
			return { ...base, notes: 'Propel Producer Profits Interest award', securityType: 'Profits Interest', sharePrice: price, strikePrice: '0' };
		case 'C': {
			const thisDate = Date.parse(dateRaw) / 1000;
			const thisQty = Number(unitsRaw.replace(/^\(/, '-').replace(/[^-.\d]/g, '')) || 0;
			const isSeller = thisQty < 0;
			const isBuyer = thisQty > 0;
			return {
				date: thisDate.toString(),
				quantity: Math.abs(thisQty).toString(),
				notes: 'Common Units held by individual',
				receiver: isSeller ? '' : receiver,
				receiverName: isSeller ? 'Buyer' : receiverName,
				securityType: 'Common Units',
				sender: isSeller ? receiver : '',
				senderName: isSeller ? receiverName : isBuyer ? 'Seller' : '',
				shareClass: 'C',
				sharePrice: price,
				strikePrice: '0',
			};
		}
		case 'C-E':
			return { ...base, notes: 'Common Units held by separate entity', securityType: 'Common Units (Entity Held)', sharePrice: price, strikePrice: '0' };
		case 'P-A':
			return { ...base, notes: 'Management Incentive Profits Interest award', securityType: 'Profits Interest', sharePrice: '0', strikePrice: price };
		case 'P-F':
			return {
				...base,
				notes: 'Management Incentive Profits Interest award',
				securityType: 'Profits Interest (Field)',
				sharePrice: '0',
				strikePrice: price,
			};
		case "P-F'":
			return {
				...base,
				notes: 'Management Incentive Profits Interest award',
				securityType: "Profits Interest (Field)'",
				sharePrice: price,
				strikePrice: '0',
			};
		default:
			return null;
	}
}

/**
 * Fetch the Alera CSV (once per session) and build a preview-scoped
 * per-class transaction map for `userid`.
 *
 * @param {Object} options
 * @param {Object} options.clientData - reads `.transactions_data` and `.transactions_columns`
 * @param {string} options.userid - previewed shareholder's email
 * @param {Object} [options.previewedUserData] - cap-table user record for receiverName
 * @param {Function} [options.getContentUrlFn] - injected for tests
 * @param {Function} [options.fetchFn] - injected for tests
 * @returns {Promise<Object|null>} { [shareClass]: Transaction[] } or null
 */
export async function fetchAleraTransactionsForPreview({ clientData, userid, previewedUserData = null, getContentUrlFn = null, fetchFn = null }) {
	if (!clientData?.transactions_data || !clientData?.transactions_columns || !userid) return null;

	const getUrl = getContentUrlFn || (typeof window !== 'undefined' ? window.getContentUrl : null);
	const doFetch = fetchFn || (typeof globalThis !== 'undefined' ? globalThis.fetch : null);
	if (!getUrl || !doFetch) {
		console.warn('[preview-alera-transactions] Missing getContentUrl or fetch — cannot load CSV');
		return null;
	}

	const cols = buildColMap(clientData.transactions_columns);
	for (const key of ['EMAIL', 'CLASS', 'PRICE', 'UNITS', 'DATE']) {
		if (cols[key] === undefined) {
			console.warn(`[preview-alera-transactions] transactions_columns missing required key: ${key}`);
			return null;
		}
	}

	const csvKey = clientData.transactions_data;
	let rows = csvRowCache.get(csvKey);
	if (!rows) {
		const url = await getUrl(csvKey);
		const response = await doFetch(url);
		const text = await response.text();
		rows = cleanCSV(text);
		csvRowCache.set(csvKey, rows);
	}

	const me = userid.toLowerCase();
	const receiverName = previewedUserData?.user_name || '';
	const result = {};

	for (const row of rows) {
		const email = row[cols.EMAIL]?.toLowerCase().trim();
		if (email !== me) continue;

		const shareClass = classifyRow(row, cols);
		if (!shareClass) continue;

		const tx = buildTransaction(row, shareClass, cols, me, receiverName);
		if (!tx) continue;

		if (!result[shareClass]) result[shareClass] = [];
		result[shareClass].push(tx);
	}

	return result;
}

/**
 * Clear the cached CSV rows. Call between tests or when the admin switches clients.
 */
export function clearPreviewAleraTransactionsCache() {
	csvRowCache.clear();
}
